import React from 'react';
import styled, { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';

import { Flex } from 'Components/Base';
import { Footer as ClassroomFooter } from 'Components/PaaS/Classroom';
import { useClassroomNavigation } from 'Util/Hooks';
import {
  genTOCEvent,
  TOC_ACTIONS,
  sendDataToGTM,
  GTM_EVENT_NAME,
  GTM_DATA_LAYER_PROPERTY,
  GTM_BUTTON_LOCATION_NAME,
} from 'Util/Analytics';
// TODO: Move sidebar content to classroom fixed view container
import { SidebarContent } from 'Routes/Classroom/ClassroomView/ClassroomFixedView';
import { useClassroomCourseData } from 'Domain/Classroom';
import { useMyCourseData, useOnlineCoursesData } from 'Domain/MyCourseData';
import { useGoToPurchase } from 'Domain/ExternalNavigation';

const DrawerWrapper = styled(Flex)`
  width: 100vw;
  height: 100%;
  /* PaaS style */
  box-shadow: 0px 0px 16px rgba(17, 17, 26, 0.1);
  border-radius: 24px 24px 0px 0px;
  overflow-y: hidden;
`;

// TODO: Create hook to check is mobile size
const Footer = ({ onLogAction, handleOpenExitModal, isMobile }) => {
  const theme = useTheme();
  const { t } = useTranslation('classroom');
  const { data: myCourseData, courseCode } = useMyCourseData();
  const { data: onlineCourseData } = useOnlineCoursesData({ courseCode: courseCode });
  const {
    goToNextSubsection,
    goToPreviousSubsection,
    isLastSubsectionInCourse,
    isFirstSubsectionInCourse,
    currentSection,
    currentSubsection,
  } = useClassroomNavigation();

  const { purchaseUrl } = useGoToPurchase(onlineCourseData?.SKUCode, {
    utmMedium: 'Purchase',
    courseTitle: myCourseData.courseInfo?.title,
    sectionTitle: currentSection.title,
    subsectionTitle: currentSubsection.title,
  });
  const { isClassroomTrialMode } = useClassroomCourseData();

  const handleGoToDiscussion = (url) => {
    window.open(url, '_blank');

    // logging GTM event
    sendDataToGTM({
      event: GTM_EVENT_NAME.DISCUSSION_BUTTON_CLICK,
      [GTM_DATA_LAYER_PROPERTY.PRODUCT_TITLE]: onlineCourseData?.title,
    });
  };

  const analyticLogger = (isOpen) => {
    const tocClickAction = isOpen ? TOC_ACTIONS.OPEN : TOC_ACTIONS.CLOSE;
    onLogAction(genTOCEvent(tocClickAction, 'TOC Button'));
  };

  const goToPurchasePage = () => {
    window.location = purchaseUrl;

    // logging GTM event
    sendDataToGTM({
      event: GTM_EVENT_NAME.TRIAL_PURCHASE_CLICK,
      [GTM_DATA_LAYER_PROPERTY.PRODUCT_TITLE]: onlineCourseData?.title,
      [GTM_DATA_LAYER_PROPERTY.BUTTON_LOCATION]: GTM_BUTTON_LOCATION_NAME.FOOTER,
    });
  };

  return (
    <ClassroomFooter
      onPrev={goToPreviousSubsection}
      onNext={isClassroomTrialMode ? goToPurchasePage : goToNextSubsection}
      isFirstSubsection={isFirstSubsectionInCourse}
      isLastSubsection={isLastSubsectionInCourse}
      drawerProps={{
        onDrawerClose: () => analyticLogger(false),
        onDrawerOpen: () => analyticLogger(true),
      }}
      isMobile={isMobile}
      isClassroomTrialMode={isClassroomTrialMode}
    >
      {(onDrawerToggle) => (
        <DrawerWrapper
          flexDirection="column"
          bg="sidebarPrimaryBackground"
          color="sidebarPrimaryText"
        >
          <SidebarContent
            sidebarContentPadding={4}
            handleGoToDiscussion={handleGoToDiscussion}
            onExitClassroom={handleOpenExitModal}
            onModalToggle={onDrawerToggle}
            theme={theme}
            translation={t}
            isMobile={isMobile}
          />
        </DrawerWrapper>
      )}
    </ClassroomFooter>
  );
};

export default Footer;
